import React, { useState, useEffect } from 'react';
import './CustomLoginPage.css';
import { CognitoErrorCode } from './CognitoAuthService';
import { GoogleLogin } from '@react-oauth/google';
import posthog from 'src/consent/posthog';

interface CustomLoginPageProps {
    loginHelper: any;
    onLoginSuccess: () => void;
    onLoginError: (error: string) => void;
    initialMode?: LoginPageMode;
}

type LoginPageMode = 'login' | 'signup' | 'forgot-password' | 'confirm-signup' | 'new-password' | 'reset-password';

interface LoginFormData {
    email: string;
    password: string;
}

interface SignUpFormData {
    email: string;
    password: string;
}

interface ForgotPasswordFormData {
    email: string;
}

const CustomLoginPage: React.FC<CustomLoginPageProps> = ({ 
    loginHelper, 
    onLoginSuccess, 
    onLoginError,
    initialMode = 'login',
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [mode, setMode] = useState<LoginPageMode>(initialMode);
    const [newPassword, setNewPassword] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [resetNewPassword, setResetNewPassword] = useState('');
    const [loginForm, setLoginForm] = useState<LoginFormData>({ email: '', password: '' });
    const [signUpForm, setSignUpForm] = useState<SignUpFormData>({
        email: '',
        password: ''
    });
    const [forgotPasswordForm, setForgotPasswordForm] = useState<ForgotPasswordFormData>({ email: '' });
    const [confirmationCode, setConfirmationCode] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isResending, setIsResending] = useState(false);

    useEffect(() => {
        setMode(initialMode);
        setError('');
        setSuccessMessage('');
    }, [initialMode]);

    // Handle cooldown timer
    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (resendCooldown > 0) {
            timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1);
            }, 1000);
        }
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const handleResendConfirmationCode = async () => {
        setIsResending(true);
        setError('');
        
        try {
            await loginHelper.resendConfirmationCode(signUpForm.email);
            setSuccessMessage('Confirmation code sent! Please check your email.');
            setResendCooldown(60); // 60 second cooldown
        } catch (err: any) {
            console.error('Resend confirmation code error:', err);
            const errorMessage = err.message || 'Failed to resend confirmation code. Please try again.';
            setError(errorMessage);
        } finally {
            setIsResending(false);
        }
    };

    const LoadingSpinner = () => (
        <div className="login-page-container">
            <div className="login-loading-container">
                <div className="login-loading-spinner"></div>
                <p className="login-loading-text">Processing...</p>
            </div>
        </div>
    );

    const redirectToConfirmSignUpIfNeeded = async (err: any) => {
        const isUserNotConfirmed = err.code === CognitoErrorCode.UserNotConfirmedException
            || err.code === CognitoErrorCode.UsernameExistsException
            || err.message === "Cannot reset password for the user as there is no registered/verified email or phone_number"

        if (isUserNotConfirmed) {
            const email = loginForm.email || signUpForm.email;
            setSignUpForm({ ...signUpForm, email });
            setMode('confirm-signup');

            try {
                await loginHelper.resendConfirmationCode(email);
                setSuccessMessage('We sent a new confirmation code to your email. Please check your inbox.');
                setResendCooldown(60);
            } catch (resendErr: any) {
                console.error('Auto-resend confirmation code error:', resendErr);
                setSuccessMessage('Your account has not been confirmed yet. Please check your email for the confirmation code or click Resend.');
            }
        }
    }

    const handleGoogleSuccess = async (credentialResponse: any) => {
        setIsLoading(true);
        setError('');

        try {
            await loginHelper.signInWithGoogleIdToken(credentialResponse.credential);
            setSuccessMessage('Login successful!');
            setTimeout(() => {
                onLoginSuccess();
            }, 1000);
        } catch (err: any) {
            console.error('Google SSO error:', err);
            setError(err.message || 'Google sign-in failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const result = await loginHelper.cognitoAuthService.signIn(loginForm.email, loginForm.password);

            if (result.newPasswordRequired) {
                setMode('new-password');
                setError('');
                setSuccessMessage('Please set a new password for your account.');
                setIsLoading(false);
                return;
            }

            if (result.success) {
                await loginHelper.syncAuthenticationState();
                posthog?.identify(loginForm.email, { email: loginForm.email, login_method: 'email' });
                posthog?.capture('user_logged_in', { login_method: 'email' });
                setSuccessMessage('Login successful!');
                setTimeout(() => {
                    onLoginSuccess();
                }, 1000);
            }
        } catch (err: any) {
            console.error('Login error:', err);
            redirectToConfirmSignUpIfNeeded(err);
            const errorMessage = err.message || 'Login failed. Please try again.';
            setError(errorMessage);
            onLoginError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const result = await loginHelper.signUp(signUpForm.email, signUpForm.password);

            if (result.result.userConfirmed) {
                posthog?.identify(signUpForm.email, { email: signUpForm.email });
                posthog?.capture('user_signed_up', { signup_method: 'email' });
                setSuccessMessage('Account created successfully! You can now log in.');
                setMode('login');
            } else {
                posthog?.capture('user_signed_up', { signup_method: 'email', confirmed: false });
                setSuccessMessage('Account created! Please check your email for confirmation code.');
                setMode('confirm-signup');
            }
        } catch (err: any) {
            console.error('Sign up error:', err);
            redirectToConfirmSignUpIfNeeded(err);
            const errorMessage = err.message || 'Sign up failed. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            await loginHelper.confirmSignUp(signUpForm.email, confirmationCode);
            setSuccessMessage('Email confirmed successfully! You can now log in.');
            setMode('login');
            setConfirmationCode('');
        } catch (err: any) {
            console.error('Confirmation error:', err);
            const errorMessage = err.message || 'Confirmation failed. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            await loginHelper.forgotPassword(forgotPasswordForm.email);
            setSuccessMessage('Password reset code sent to your email. Enter it below with your new password.');
            setMode('reset-password');
        } catch (err: any) {
            console.error('Forgot password error:', err);
            if (err.message.includes('no registered/verified')) {
                // Switch to confirm-signup mode and preserve the email
                setSignUpForm({ ...signUpForm, email: loginForm.email });
                setMode('confirm-signup');
                setSuccessMessage('Your account has not been confirmed yet. Please check your email for the confirmation code.');
                return;
            }
            const errorMessage = err.message || 'Failed to send reset code. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNewPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const result = await loginHelper.cognitoAuthService.completeNewPassword(newPassword);
            if (result.success) {
                await loginHelper.syncAuthenticationState();
                setSuccessMessage('Password set successfully!');
                setTimeout(() => {
                    onLoginSuccess();
                }, 1000);
            }
        } catch (err: any) {
            console.error('New password error:', err);
            const errorMessage = err.message || 'Failed to set password. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            await loginHelper.cognitoAuthService.resetPassword(forgotPasswordForm.email, resetCode, resetNewPassword);
            setSuccessMessage('Password reset successfully! You can now sign in.');
            setMode('login');
            setResetCode('');
            setResetNewPassword('');
        } catch (err: any) {
            console.error('Reset password error:', err);
            const errorMessage = err.message || 'Failed to reset password. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const renderNewPasswordForm = () => (
        <form onSubmit={handleNewPassword} className="login-form">
            <div className="login-form-group">
                <label htmlFor="new-password" className="login-form-label">
                    New Password
                </label>
                <input
                    type="password"
                    id="new-password"
                    name="new-password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="login-form-input"
                    placeholder="Choose a new password"
                    required
                />
                <p className="login-form-hint">
                    Must be at least 8 characters with uppercase, lowercase, numbers, and symbols.
                </p>
            </div>
            <button
                type="submit"
                disabled={isLoading}
                className="login-primary-button"
            >
                {isLoading ? 'Setting Password...' : 'Set Password & Log In'}
            </button>
        </form>
    );

    const renderLoginForm = () => (
        <form onSubmit={handleLogin} className="login-form">
            <div className="login-google-button">
                <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError('Google sign-in failed. Please try again.')}
                    width="100%"
                    text="signin_with"
                />
            </div>
            <div className="login-divider">
                <span>or</span>
            </div>
            <div className="login-form-group">
                <label htmlFor="email" className="login-form-label">
                    Email Address
                </label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="login-form-input"
                    required
                />
            </div>
            <div className="login-form-group">
                <label htmlFor="password" className="login-form-label">
                    Password
                </label>
                <input
                    type="password"
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="login-form-input"
                    required
                />
            </div>
            <button
                type="submit"
                disabled={isLoading}
                className="login-primary-button"
            >
                {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
            <div className="login-form-links">
                <button
                    type="button"
                    onClick={() => { setMode('forgot-password'); setError(''); setSuccessMessage(''); }}
                    className="login-link-button"
                >
                    Forgot your password?
                </button>
                <div className="login-form-switch">
                    Don't have an account?{' '}
                    <button
                        type="button"
                        onClick={() => { setMode('signup'); setError(''); setSuccessMessage(''); }}
                        className="login-link-button"
                    >
                        Sign up
                    </button>
                </div>
            </div>
        </form>
    );

    const renderSignUpForm = () => (
        <form onSubmit={handleSignUp} className="login-form">
            <div className="login-google-button">
                <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError('Google sign-in failed. Please try again.')}
                    width="100%"
                    text="signup_with"
                />
            </div>
            <div className="login-divider">
                <span>or</span>
            </div>
            <div className="login-form-group">
                <label htmlFor="signup-email" className="login-form-label">
                    Email Address
                </label>
                <input
                    type="email"
                    id="signup-email"
                    name="email"
                    autoComplete="email"
                    value={signUpForm.email}
                    onChange={(e) => setSignUpForm({ ...signUpForm, email: e.target.value })}
                    className="login-form-input"
                    required
                />
            </div>
            <div className="login-form-group">
                <label htmlFor="signup-password" className="login-form-label">
                    Password
                </label>
                <input
                    type="password"
                    id="signup-password"
                    name="new-password"
                    autoComplete="new-password"
                    value={signUpForm.password}
                    onChange={(e) => setSignUpForm({ ...signUpForm, password: e.target.value })}
                    className="login-form-input"
                    required
                />
                <p className="login-form-hint">
                    Password must be at least 8 characters with uppercase, lowercase, numbers, and symbols.
                </p>
            </div>
            <button
                type="submit"
                disabled={isLoading}
                className="login-primary-button login-signup-button"
            >
                {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
            <div className="login-form-switch">
                <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); setSuccessMessage(''); }}
                    className="login-link-button"
                >
                    Already have an account? Sign in
                </button>
            </div>
        </form>
    );

    const renderConfirmSignUpForm = () => (
        <form onSubmit={handleConfirmSignUp} className="login-form">
            <div className="login-form-group">
                <label htmlFor="confirmation-code" className="login-form-label">
                    Confirmation Code
                </label>
                <input
                    type="text"
                    id="confirmation-code"
                    name="one-time-code"
                    autoComplete="one-time-code"
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    className="login-form-input"
                    placeholder="Enter code from email"
                    required
                />
            </div>
            <button
                type="submit"
                disabled={isLoading}
                className="login-primary-button"
            >
                {isLoading ? 'Confirming...' : 'Confirm Account'}
            </button>
            
            <button
                type="button"
                onClick={handleResendConfirmationCode}
                disabled={isResending || resendCooldown > 0}
                className="login-primary-button"
            >
                {isResending ? 'Sending...' : 
                 resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 
                 'Resend'}
            </button>
            
            <div className="login-form-switch">
                <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); setSuccessMessage(''); }}
                    className="login-link-button"
                >
                    Back to Sign In
                </button>
            </div>
        </form>
    );

    const renderForgotPasswordForm = () => (
        <form onSubmit={handleForgotPassword} className="login-form">
            <div className="login-form-group">
                <label htmlFor="forgot-email" className="login-form-label">
                    Email Address
                </label>
                <input
                    type="email"
                    id="forgot-email"
                    name="email"
                    autoComplete="email"
                    value={forgotPasswordForm.email}
                    onChange={(e) => setForgotPasswordForm({ email: e.target.value })}
                    className="login-form-input"
                    required
                />
            </div>
            <button
                type="submit"
                disabled={isLoading}
                className="login-primary-button"
            >
                {isLoading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <div className="login-form-switch">
                <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); setSuccessMessage(''); }}
                    className="login-link-button"
                >
                    Back to Sign In
                </button>
            </div>
        </form>
    );

    const renderResetPasswordForm = () => (
        <form onSubmit={handleResetPassword} className="login-form">
            <div className="login-form-group">
                <label htmlFor="reset-code" className="login-form-label">
                    Reset Code
                </label>
                <input
                    type="text"
                    id="reset-code"
                    name="one-time-code"
                    autoComplete="one-time-code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    className="login-form-input"
                    placeholder="Enter code from email"
                    required
                />
            </div>
            <div className="login-form-group">
                <label htmlFor="reset-new-password" className="login-form-label">
                    New Password
                </label>
                <input
                    type="password"
                    id="reset-new-password"
                    name="new-password"
                    autoComplete="new-password"
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    className="login-form-input"
                    placeholder="Choose a new password"
                    required
                />
                <p className="login-form-hint">
                    Must be at least 8 characters with uppercase, lowercase, numbers, and symbols.
                </p>
            </div>
            <button
                type="submit"
                disabled={isLoading}
                className="login-primary-button"
            >
                {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
            <div className="login-form-switch">
                <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); setSuccessMessage(''); }}
                    className="login-link-button"
                >
                    Back to Sign In
                </button>
            </div>
        </form>
    );

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="login-page-container">
            <div className="login-content-container">
                <div className="login-header">
                    <h2 className="login-title">
                        {mode === 'login' && 'Sign in to your account'}
                        {mode === 'signup' && 'Create your account'}
                        {mode === 'confirm-signup' && 'Confirm your account'}
                        {mode === 'forgot-password' && 'Reset your password'}
                        {mode === 'new-password' && 'Set your new password'}
                        {mode === 'reset-password' && 'Reset your password'}
                    </h2>
                    <p className="login-subtitle">
                        {mode === 'login' && 'Welcome back to HomeIndex'}
                        {mode === 'signup' && 'Join HomeIndex today'}
                        {mode === 'confirm-signup' && 'Check your email for the confirmation code'}
                        {mode === 'forgot-password' && 'Enter your email to receive a reset code'}
                        {mode === 'new-password' && 'Choose a password to complete your account setup'}
                        {mode === 'reset-password' && 'Enter the code from your email and choose a new password'}
                    </p>
                </div>
                
                <div className="login-form-container">
                    {error && (
                        <div className="login-error-message">
                            {error}
                        </div>
                    )}
                    
                    {successMessage && (
                        <div className="login-success-message">
                            {successMessage}
                        </div>
                    )}

                    {mode === 'login' && renderLoginForm()}
                    {mode === 'signup' && renderSignUpForm()}
                    {mode === 'confirm-signup' && renderConfirmSignUpForm()}
                    {mode === 'forgot-password' && renderForgotPasswordForm()}
                    {mode === 'reset-password' && renderResetPasswordForm()}
                    {mode === 'new-password' && renderNewPasswordForm()}
                </div>
            </div>
        </div>
    );
};

export default CustomLoginPage;
